import { podoFetcher, toLegacyPodoErrorResponse } from '@server/infrastructure/external-apis/podo/client'
import { toCamelCase } from '@server/shared/utils/case-adapter'

import { getBearerToken } from '../../authentication'
import type {
  SelfHoldingConflictItem,
  SelfHoldingMutationResult,
  SelfHoldingsDto,
  SubscribeMappDetailDto,
  SubscribeMappListDto,
  TicketSubscribeDetail,
  TicketSubscribeListAll,
} from '../model/plan.types'

interface LegacyListResponse {
  resultCd: string
  resultMsg?: string
  result: TicketSubscribeListAll[]
}

interface LegacyDetailResponse {
  resultCd: string
  resultMsg?: string
  result: TicketSubscribeDetail
}

export const getTicketSubscribeListAll = async (bearerToken?: string): Promise<TicketSubscribeListAll[]> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  try {
    const response = await podoFetcher<LegacyListResponse>({
      url: '/api/v1/subscribe/podo/getTicketSubscribeList?listType=ALL',
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })
    return response.data
  } catch (error) {
    const response = toLegacyPodoErrorResponse<TicketSubscribeListAll[]>(error)

    if (response?.resultCd === '404') {
      return response.result ?? []
    }

    throw error
  }
}

export const getTicketSubscribeDetail = async (
  ticketIds: string,
  bearerToken?: string,
): Promise<TicketSubscribeDetail> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<LegacyDetailResponse>({
    url: `/api/v1/subscribe/podo/getTicketSubscribeDetail?ticketIds=${ticketIds}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return response.data
}

export type RequestCertificatePurpose = 'COMPANY' | 'ACADEMIC' | 'VISA' | 'ETC'

export type RequestCertificateParams = Record<string, unknown> & {
  ticketId: string
  subscribeMappId: string
  purpose: RequestCertificatePurpose
  // 이메일 발송 폐지 → 인앱 다운로드 전환: 수신 이메일/목적 상세는 더 이상 수집하지 않으므로 optional.
  recipientEmail?: string
  isCompanyEmail?: boolean
  isRecipientModified?: boolean
  purposeDetail?: Record<string, string>
}

interface RequestCertificateResponse {
  resultCd: string
  resultMsg?: string
}

interface SubscribeMappListResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: SubscribeMappListDto[]
}

interface SubscribeMappDetailResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: SubscribeMappDetailDto
}

interface SelfHoldingsResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: SelfHoldingsDto
}

interface SelfHoldingMutationResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: SelfHoldingMutationResult
}

interface SelfHoldingConflictsResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: SelfHoldingConflictItem[]
}

export const getSubscribeMapp = async (bearerToken?: string): Promise<SubscribeMappListDto[]> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SubscribeMappListResponse>({
    url: '/api/v1/subscribe/podo/subscribe-mapp',
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SubscribeMappListDto[]>(response.data)
}

export const getSubscribeMappDetail = async (
  subscribeMappId: string,
  bearerToken?: string,
): Promise<SubscribeMappDetailDto> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SubscribeMappDetailResponse>({
    url: `/api/v1/subscribe/podo/subscribe-mapp/${subscribeMappId}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SubscribeMappDetailDto>(response.data)
}

export const getSelfHoldings = async (subscribeMappId: string, bearerToken?: string): Promise<SelfHoldingsDto> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SelfHoldingsResponse>({
    url: `/api/v1/subscribe/podo/subscribe-mapp/${subscribeMappId}/holdings`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SelfHoldingsDto>(response.data)
}

export interface CreateSelfHoldingParams {
  subscribeMappId: string
  startDate: string
  endDate: string
}

export const getSelfHoldingConflicts = async (
  { subscribeMappId, startDate, endDate }: CreateSelfHoldingParams,
  bearerToken?: string,
): Promise<SelfHoldingConflictItem[]> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const query = new URLSearchParams({ start_date: startDate, end_date: endDate })
  const response = await podoFetcher<SelfHoldingConflictsResponse>({
    url: `/api/v1/subscribe/podo/subscribe-mapp/${subscribeMappId}/holdings/conflicts?${query.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SelfHoldingConflictItem[]>(response.data)
}

export const createSelfHolding = async (
  { subscribeMappId, startDate, endDate }: CreateSelfHoldingParams,
  bearerToken?: string,
): Promise<SelfHoldingMutationResult> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SelfHoldingMutationResponse>({
    url: `/api/v1/subscribe/podo/subscribe-mapp/${subscribeMappId}/holdings`,
    method: 'POST',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
    body: {
      start_date: startDate,
      end_date: endDate,
    },
  })
  return toCamelCase<SelfHoldingMutationResult>(response.data)
}

export interface CancelSelfHoldingParams {
  subscribeMappId: string
  holdingId: number
}

export const cancelSelfHolding = async (
  { subscribeMappId, holdingId }: CancelSelfHoldingParams,
  bearerToken?: string,
): Promise<SelfHoldingMutationResult> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SelfHoldingMutationResponse>({
    url: `/api/v1/subscribe/podo/subscribe-mapp/${subscribeMappId}/holdings/${holdingId}`,
    method: 'DELETE',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SelfHoldingMutationResult>(response.data)
}

interface ChangeActivationOrderResponse {
  resultCd: number
  resultCdName: string
  message: string | null
  data: boolean
}

export const changeActivationOrder = async (
  orderedSubscribeMappIds: string[],
  bearerToken?: string,
): Promise<boolean> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<ChangeActivationOrderResponse>({
    url: '/api/v1/subscribe/podo/activation-order',
    method: 'PUT',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
    body: { ordered_subscribe_mapp_ids: orderedSubscribeMappIds },
  })
  return response.data
}

export const requestCertificate = async (
  params: RequestCertificateParams,
  bearerToken?: string,
): Promise<RequestCertificateResponse> => {
  try {
    const authorizationToken = bearerToken ?? (await getBearerToken())
    const response = await podoFetcher<RequestCertificateResponse>({
      url: '/api/v1/subscribe/podo/certificate-issuance',
      method: 'POST',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
      body: params,
    })

    return response.data
  } catch (error) {
    console.error('[requestCertificate] API request failed:', error)
    throw new Error('CERTIFICATE_REQUEST_FAILED')
  }
}
